import { chartScale, lineGeometry } from "../growth";

const WIDTH = 640;
const HEIGHT = 180;
// Room for the y labels on the left and the x labels below.
const LEFT = 44;
const BOTTOM = 20;
const TOP = 6;

export interface LineSeries {
  key: string;
  label: string;
  values: readonly (number | null)[];
  /** A categorical slot (`series-1`…`series-5`, fixed per entity) or the muted ink. */
  tone: `series-${1 | 2 | 3 | 4 | 5}` | "muted";
}

/**
 * A time series chart (DESIGN.md decision 9; hand-rolled SVG like the
 * sparklines): 2 px lines on one y-scale from zero, recessive gridlines, a
 * legend for two or more series, a tooltip per bucket listing every series,
 * gaps where the rollup built nothing.
 */
export function LineChart({
  title,
  note,
  buckets,
  series,
  bucketLabel,
  format = (value) => value.toLocaleString(),
}: {
  title: string;
  note?: string;
  buckets: readonly string[];
  series: readonly LineSeries[];
  bucketLabel: (bucket: string) => string;
  format?: (value: number) => string;
}) {
  const plotWidth = WIDTH - LEFT;
  const plotHeight = HEIGHT - BOTTOM - TOP;
  const scale = chartScale(series.map((s) => s.values));
  const geometry = lineGeometry(
    series.map((s) => s.values),
    scale,
    plotWidth,
    plotHeight,
  );
  const slot = plotWidth / Math.max(1, buckets.length);
  const labelled = [0, Math.floor((buckets.length - 1) / 2), buckets.length - 1].filter(
    (index, i, all) => index >= 0 && all.indexOf(index) === i,
  );
  return (
    <figure className="chart line-chart">
      <figcaption>
        {title} {note && <span className="muted">· {note}</span>}
      </figcaption>
      {series.length > 1 && (
        <ul className="legend">
          {series.map((s) => (
            <li key={s.key}>
              <span className={`swatch tone-${s.tone}`} aria-hidden="true" />
              {s.label}
            </li>
          ))}
        </ul>
      )}
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={`${title}; the table view lists every value`}>
        <g transform={`translate(${LEFT},${TOP})`}>
          {scale.ticks.map((tick) => (
            <g key={tick}>
              <line
                className={tick === 0 ? "chart-axis" : "chart-grid"}
                x1={0}
                x2={plotWidth}
                y1={geometry.y(tick)}
                y2={geometry.y(tick)}
              />
              <text className="chart-label" x={-6} y={geometry.y(tick)} textAnchor="end" dominantBaseline="middle">
                {format(tick)}
              </text>
            </g>
          ))}
          {labelled.map((index) => (
            <text
              key={buckets[index]}
              className="chart-label"
              x={geometry.xs[index]}
              y={plotHeight + 15}
              textAnchor={index === 0 ? "start" : index === buckets.length - 1 ? "end" : "middle"}
            >
              {bucketLabel(buckets[index]!)}
            </text>
          ))}
          {series.map((s, i) =>
            geometry.runs[i]!.map((points) =>
              points.includes(" ") ? (
                <polyline key={`${s.key}:${points}`} className={`chart-line tone-${s.tone}`} points={points} />
              ) : (
                <circle
                  key={`${s.key}:${points}`}
                  className={`chart-point tone-${s.tone}`}
                  cx={points.split(",")[0]}
                  cy={points.split(",")[1]}
                  r={3}
                />
              ),
            ),
          )}
          {/* Full-height hover targets, one per bucket, carry the tooltips. */}
          {buckets.map((bucket, index) => (
            <rect
              key={bucket}
              className="chart-hit"
              x={(geometry.xs[index] ?? 0) - slot / 2}
              y={0}
              width={slot}
              height={plotHeight}
            >
              <title>
                {[
                  bucketLabel(bucket),
                  ...series.map((s) => {
                    const value = s.values[index];
                    return `${s.label}: ${value === null || value === undefined ? "not built" : format(value)}`;
                  }),
                ].join("\n")}
              </title>
            </rect>
          ))}
        </g>
      </svg>
    </figure>
  );
}
